import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api';
import '../styles/MiscellaneousExpenses.css';

const MiscellaneousExpenses = () => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    given_by: '',
    given_to: '',
    description: '',
    custom_description: '',
    total_amount: '',
    transaction_type: ''
  });

  const [expenses, setExpenses] = useState([]);
  const [lastSavedExpense, setLastSavedExpense] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // Format date as DD/MM/YYYY for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const fetchExpenses = async () => {
    const res = await apiFetch('get_misc_expenses.php');
    const data = await res.json();
    setExpenses(data);
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalData = {
      ...formData,
      description: formData.description === 'Others' ? formData.custom_description : formData.description
    };

    const res = await apiFetch('add_misc_expense.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(finalData),
      credentials: 'include'
    });

    const result = await res.json();
    alert(result.message);

    setLastSavedExpense(finalData);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      given_by: '',
      given_to: '',
      description: '',
      custom_description: '',
      total_amount: '',
      transaction_type: ''
    });
    fetchExpenses();
  };

  const printBill = () => {
    const data = lastSavedExpense;
    const billContent = `
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; }
        .page { display: flex; justify-content: space-between; padding: 15mm; }
        .bill {
          width: 48%; border: 1px solid #000; padding: 15px; box-sizing: border-box;
        }
        .bill-header { text-align: center; margin-bottom: 5px; }
        .school-name { font-size: 14px; font-weight: bold; }
        .school-address { font-size: 11px; margin-top: 4px; }
        .bill-title { font-size: 12px; font-weight: bold; margin-top: 8px; }
        .row { margin: 6px 0; display: flex; justify-content: space-between; }
        .sign { margin-top: 20px; text-align: right; font-size: 11px; }
      </style>
  
      <div class="page">
        ${[1, 2].map(() => `
          <div class="bill">
            <div class="bill-header">
              <img class="school-logo" src="${window.location.origin}/school-logo.jpg" alt="Logo" width="50" height="50" style="float: left;"/>
              <div class="school-name">OXFORD CONVENT<br>ENGLISH MEDIUM SCHOOL, KHAJURI</div>
              <div class="school-address">KHAJURI - 585 314 - Tq. Aland Dist. Kalaburagi</div>
              <div class="bill-title">MISCELLANEOUS EXPENSE BILL</div>
            </div>
  
            <div class="row"><strong>Date:</strong> ${formatDate(data.date)}</div>
            <div class="row"><strong>Given By:</strong> ${data.given_by}</div>
            <div class="row"><strong>Given To:</strong> ${data.given_to}</div>
            <div class="row"><strong>Description:</strong> ${data.description}</div>
            <div class="row"><strong>Transaction Type:</strong> ${data.transaction_type}</div>
            <div class="row" style="font-weight: bold;"><strong>Total Amount:</strong> ₹${data.total_amount}</div>
  
            <div class="sign">Sign of Admin / Cashier</div>
          </div>
        `).join('')}
      </div>
      
      <script>
        window.onload = function() {
          const logos = document.querySelectorAll('.school-logo');
          const allComplete = Array.from(logos).every(img => img.complete);
          if (allComplete) window.print();
          else {
            Promise.all(Array.from(logos).map(logo => new Promise(resolve => {
              logo.onload = resolve;
              logo.onerror = resolve;
            }))).then(() => window.print());
          }
        };
      </script>
    `;

    const billWindow = window.open('', '', 'height=800,width=1200');
    billWindow.document.write('<html><head><title>Expense Bill</title></head><body>');
    billWindow.document.write(billContent);
    billWindow.document.write('</body></html>');
    billWindow.document.close();
  };

  // Get unique descriptions for suggestions
  const uniqueDescriptions = React.useMemo(() => {
    const descriptions = expenses.map(exp => exp.description).filter(Boolean);
    return [...new Set(descriptions)].sort();
  }, [expenses]);

  // Filter suggestions based on search input
  const filteredSuggestions = React.useMemo(() => {
    if (!searchDescription.trim()) return [];
    const searchLower = searchDescription.toLowerCase();
    return uniqueDescriptions
      .filter(desc => desc.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [searchDescription, uniqueDescriptions]);

  const filteredExpenses = React.useMemo(() => {
    if (!expenses.length) return [];
    return expenses
      .filter(exp => {
        const d = new Date(exp.date);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate && d > new Date(toDate)) return false;
        if (searchDescription.trim()) {
          const searchLower = searchDescription.toLowerCase();
          const descLower = (exp.description || '').toLowerCase();
          if (!descLower.includes(searchLower)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [expenses, fromDate, toDate, searchDescription]);

  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.total_amount || 0), 0);

  const printHistory = () => {
    if (!filteredExpenses.length) return;
    let titleText = 'Expense History';
    if (fromDate && toDate) {
      titleText = `Expense History (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    } else if (searchDescription.trim()) {
      titleText = `Expense History - ${searchDescription}`;
    }
    if (fromDate && toDate && searchDescription.trim()) {
      titleText = `Expense History - ${searchDescription} (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    }

    const tableRows = filteredExpenses.map(exp => `
      <tr>
        <td>${formatDate(exp.date)}</td>
        <td>${exp.description}</td>
        <td>${exp.given_to}</td>
        <td>₹${exp.total_amount}</td>
      </tr>
    `).join('');

    const content = `
      <html>
        <head>
          <title>${titleText}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 13px; margin: 30px; }
            .history-title { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 5px;}
            .history-period { text-align: center; font-size: 15px; color: #666; margin-bottom: 16px;}
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #555; padding: 8px 5px; text-align: left; }
            th { background: #f1f1f1; }
            .school-logo { display: block; margin: 0 auto 6px auto; }
          </style>
        </head>
        <body>
          <img id="schoolLogo" src="/school-logo.jpg" alt="School Logo" width="60" height="60" class="school-logo" />
          <div class="history-title">OXFORD CONVENT ENGLISH MEDIUM SCHOOL, KHAJURI</div>
          <div class="history-period">${titleText}</div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Given To</th>
                <th>Total Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              <tr>
                <td colspan="3" style="text-align: right; font-weight: bold;">Total</td>
                <td style="font-weight: bold;">₹${totalAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          <script>
            window.onload = function() {
              const logo = document.getElementById('schoolLogo');
              if (logo.complete) window.print();
              else {
                logo.onload = () => window.print();
                logo.onerror = () => window.print();
              }
            };
          </script>
        </body>
      </html>
    `;

    const win = window.open('', '', 'height=800,width=1200');
    win.document.write(content);
    win.document.close();
  };

  return (
    <div className="mex-container">
      <div className="mex-card">
        <h2 className="mex-title">🧾 Miscellaneous Expenses</h2>
        <form onSubmit={handleSubmit} className="mex-form">
          <div className="mex-group">
            <label>Date (DD/MM/YYYY)</label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} required />
          </div>

          <div className="mex-group">
            <label>Given By</label>
            <input type="text" name="given_by" value={formData.given_by} onChange={handleChange} required />
          </div>

          <div className="mex-group">
            <label>Given To</label>
            <input type="text" name="given_to" value={formData.given_to} onChange={handleChange} required />
          </div>

          <div className="mex-group">
            <label>Description</label>
            <select name="description" value={formData.description} onChange={handleChange} required>
              <option value="">-- Select Description --</option>
              <option value="Van petrol">Van petrol</option>
              <option value="Van repair">Van repair</option>
              <option value="Electricity Bill">Electricity Bill</option>
              <option value="Van Insurance">Van Insurance</option>
              <option value="Annual Day expenses">Annual Day expenses</option>
              <option value="Independence Day Expenses">Independence Day Expenses</option>
              <option value="Republic Day Expenses">Republic Day Expenses</option>
              <option value="All Jayanti Expenses">All Jayanti Expenses</option>
              <option value="Newspaper Expenses">Newspaper Expenses</option>
              <option value="Office Material Expenses">Office Material Expenses</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {formData.description === 'Others' && (
            <div className="mex-group">
              <label>Custom Description</label>
              <input
                type="text"
                name="custom_description"
                placeholder="Enter custom description"
                value={formData.custom_description}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="mex-group">
            <label>Total Amount</label>
            <input type="number" name="total_amount" value={formData.total_amount} onChange={handleChange} required />
          </div>

          <div className="mex-group">
            <label>Transaction Type</label>
            <select name="transaction_type" value={formData.transaction_type} onChange={handleChange} required>
              <option value="">-- Select Type --</option>
              <option value="CASH">CASH</option>
              <option value="UPI">UPI</option>
              <option value="CHEQUE">CHEQUE</option>
            </select>
          </div>

          <button type="submit" className="mex-button">Save Expense</button>
        </form>

        {lastSavedExpense && (
          <button onClick={printBill} className="mex-button" style={{ marginTop: '10px' }}>
            🖨️ Print Bill
          </button>
        )}
      </div>

      <div className="mex-card">
        <h3 className="mex-title">📜 Expense History</h3>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ position: 'relative', marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 600, color: '#333' }}>
              Search by Description:
            </label>
            <input
              type="text"
              value={searchDescription}
              onChange={(e) => {
                setSearchDescription(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={(e) => {
                // Don't close if clicking inside suggestions dropdown
                if (suggestionsRef.current && suggestionsRef.current.contains(e.relatedTarget)) {
                  return;
                }
                setTimeout(() => {
                  if (!suggestionsRef.current || !suggestionsRef.current.contains(document.activeElement)) {
                    setShowSuggestions(false);
                  }
                }, 200);
              }}
              placeholder="Type to search description..."
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxSizing: 'border-box'
              }}
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '2px'
                }}
              >
                {filteredSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      setSearchDescription(suggestion);
                      setShowSuggestions(false);
                    }}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      borderBottom: idx < filteredSuggestions.length - 1 ? '1px solid #eee' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f8ff'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <div>
            <label>From (DD/MM/YYYY): </label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ padding: '3px 7px', fontSize: 13 }} />
          </div>
          <div>
            <label>To (DD/MM/YYYY): </label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ padding: '3px 7px', fontSize: 13 }} />
          </div>
          {searchDescription.trim() && (
            <button
              onClick={() => setSearchDescription('')}
              style={{
                padding: '4px 10px',
                fontSize: 13,
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Clear Search
            </button>
          )}
          <button
            className="mex-button"
            style={{ padding: '4px 10px', fontSize: 13, marginLeft: 'auto' }}
            onClick={printHistory}
            disabled={filteredExpenses.length === 0}
          >
            🖨️ Print
          </button>
        </div>

        <div className="mex-table-wrapper">
          <table className="mex-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Given To</th>
                <th>Total Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: '#aaa' }}>No expense records found.</td>
                </tr>
              ) : (
                <>
                  {filteredExpenses.map((exp, index) => (
                    <tr key={index}>
                      <td>{formatDate(exp.date)}</td>
                      <td>{exp.description}</td>
                      <td>{exp.given_to}</td>
                      <td>₹{exp.total_amount}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                    <td style={{ fontWeight: 'bold' }}>₹{totalAmount.toFixed(2)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MiscellaneousExpenses;
